import { isFunction } from 'lodash';
import { assign, createMachine } from 'xstate';
import { LDClient, LDFlags } from '~/providers';
import { agreeToTerms } from '~/services';
import { fetchTermsBase } from '~/services/fetch-vnm-terms/utils';
import { ContractVersion } from '~/typings/context';
import { fetchContractLanguage } from '~/utils/fetch-contract-language';
import { sendToSegment } from '~/utils/tracking';
import { setError } from '../utils/actions';
import { contextCreator } from '../utils/context';
import { LegalContext, LegalDoneData, LegalEvent, LegalServices, LegalStates } from './types';

export const legalMachine = () =>
  createMachine(
    {
      tsTypes: {} as import('./machine.typegen').Typegen0,
      predictableActionArguments: true,
      schema: {
        context: {} as LegalContext,
        events: {} as LegalEvent,
        services: {} as LegalServices,
      },
      context: {
        errors: {},
        funnelPacket: {} as LegalContext['funnelPacket'],
        showLmi: false,
        funnelId: '',
        vnmSolarProjectContracts: [] as ContractVersion[],
      },
      id: 'legalMachine',
      initial: 'start',
      states: {
        start: {
          entry: 'trackStarted',
          always: { target: 'review' },
        },
        review: {
          initial: 'fetching',
          states: {
            fetching: {
              always: [{ cond: 'noTermsNeeded', target: 'idle' }],
              invoke: {
                id: 'fetchTerms',
                src: 'fetchTerms',
                onDone: { target: 'idle', actions: 'saveFetchTermsData' },
                onError: { target: 'idle', actions: 'setLegalError' },
              },
            },
            idle: {
              on: {
                SUBMIT_TERMS: [
                  {
                    cond: 'noTermsNeeded',
                    target: '#completed',
                    actions: 'setSelfAttestationData',
                  },
                  {
                    target: 'submitting',
                    actions: ['saveLmiStatus', 'setSelfAttestationData'],
                  },
                ],
              },
            },
            submitting: {
              entry: 'clearLegalError',
              invoke: {
                id: 'signTerms',
                src: 'signTerms',
                onDone: { target: '#completed' },
                onError: { target: 'idle', actions: 'setLegalError' },
              },
            },
          },
        },
        completed: {
          id: 'completed',
          type: 'final',
          data: (context): LegalDoneData => ({
            docuSignContracts: context.docuSignContracts,
            vnmSolarProjectContracts: context.vnmSolarProjectContracts,
            lmiStatus: context.lmiStatus,
            selfAttestationData: context.selfAttestationData,
          }),
          entry: 'trackCompleted',
        },
      },
    },
    {
      /* eslint-disable @typescript-eslint/no-unused-vars */
      // TLDR: need the unused _context - https://xstate.js.org/docs/guides/typescript.html#troubleshooting
      actions: {
        clearLegalError: assign(context => ({
          errors: { ...context.errors, review: undefined },
        })),
        saveFetchTermsData: assign((_context, event) => ({
          docuSignContracts: event.data.docuSignContracts,
          showLmi: event.data.showLmi,
          vnmSolarProjectContracts: event.data.vnmSolarProjectContracts,
        })),
        setLegalError: assign((context, event) => ({
          errors: setError<LegalStates>('review', context, event),
        })),
        saveLmiStatus: assign((_context, event) => ({
          lmiStatus: event.data?.lmiStatus,
        })),
        setSelfAttestationData: assign((_context, event) => ({
          selfAttestationData: event.data?.selfAttestationData,
        })),
        trackCompleted: context => {
          sendToSegment({
            type: 'legal_step_completed',
            contractIds: context.vnmSolarProjectContracts?.map(c => c.id) || [],
            funnelPacket: context.funnelPacket,
          });
        },
        trackStarted: context => {
          sendToSegment({
            type: 'legal_step_started',
            funnelPacket: context.funnelPacket,
          });
        },
      },
      guards: {
        noTermsNeeded: () => false,
      },
      services: {
        fetchTerms: async context => {
          const contractTags = isFunction(context.contractTags)
            ? context.contractTags(context)
            : context.contractTags;
          const { docuSignContracts, unsignedSolarContracts, showLmiFromTerms } =
            await fetchTermsBase({
              stateId: context.funnelPacket.utility.state.id,
              utilityAccountId: context.funnelPacket.utilityAccount.id,
              signedContractIds: context.funnelPacket?.signedContractIds || [],
              funnelId: context.funnelId,
              ...(contractTags ? { contractTags } : {}),
            });

          const showLmi = showLmiFromTerms && lmiUtilityRules(context);

          const data = fetchContractLanguage(docuSignContracts, unsignedSolarContracts, showLmi);
          return data;
        },
        signTerms: async context => {
          await agreeToTerms({
            userId: context.funnelPacket.user.id,
            utilityAccountId: context.funnelPacket.utilityAccount.id,
            vnmSolarProjectContracts: context.vnmSolarProjectContracts,
          });
          return undefined;
        },
      },
    },
  );
export const legalContext = contextCreator<LegalContext, 'funnelPacket'>(legalMachine);

export const lmiUtilityRules = (context: LegalContext): boolean => {
  const stateAbbreviation = context.funnelPacket?.utility.state.abbreviation;

  switch (stateAbbreviation) {
    case 'NJ':
      return (
        !LDClient.variation(LDFlags.showLmiSelfAttestationPostPayment) ||
        !(localStorage.getItem('showLmiSelfAttestationPostPayment') === 'true')
      );
    case 'NY':
      return !context.isNYLMI;
    case 'MD':
      return !LDClient.variation(LDFlags.ffUseGeoCoderForMd);
    case 'DE':
      return false;
    default:
      return true;
  }
};
